/**
 * Phase state machine for AddToCollectionModal / EditCollectionCardModal: search → edition → details.
 */
import { usePrevious } from '@uidotdev/usehooks';
import { useCallback, useEffect, useMemo, useState } from 'react';

import useCardIndex from '@/hooks/useCardIndex';
import type { MinimalCard } from '@/services/cardDatabase';

export type AddCardPhase = 'search' | 'edition' | 'details';

export interface UseAddToCollectionFlowOptions {
  isOpen: boolean;
  /** Printing to start from; skips (and hides) the search phase */
  preSelectedCard?: MinimalCard | null;
  /** Phase to open on when pre-selected (default 'details'); 'edition' falls back to details for single-printing cards */
  initialPhase?: 'edition' | 'details';
}

const isSameCard = (a: MinimalCard | null, b: MinimalCard) =>
  a?.oracle_id && b.oracle_id ? a.oracle_id === b.oracle_id : a?.name === b.name;

export default function useAddToCollectionFlow({ isOpen, preSelectedCard, initialPhase = 'details' }: UseAddToCollectionFlowOptions) {
  const { getAllPrintingsByName } = useCardIndex();
  const isPreSelected = Boolean(preSelectedCard);

  const getStartPhase = (): AddCardPhase => {
    if (!preSelectedCard) return 'search';
    if (initialPhase === 'edition' && getAllPrintingsByName(preSelectedCard.name).length > 1) return 'edition';
    return 'details';
  };

  const [phase, setPhase] = useState<AddCardPhase>(getStartPhase);
  // Card chosen in search (any printing); identifies the card across phases
  const [selectedCard, setSelectedCard] = useState<MinimalCard | null>(preSelectedCard ?? null);
  const [selectedPrinting, setSelectedPrinting] = useState<MinimalCard | null>(preSelectedCard ?? null);
  // Bumped to remount (and so clear) the search step
  const [searchResetKey, setSearchResetKey] = useState(0);

  const prevIsOpen = usePrevious(isOpen);
  useEffect(() => {
    if (!isOpen || prevIsOpen) return;
    setPhase(getStartPhase());
    setSelectedCard(preSelectedCard ?? null);
    setSelectedPrinting(preSelectedCard ?? null);
  }, [isOpen, prevIsOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedName = selectedCard?.name;
  const printings = useMemo(() => (selectedName ? getAllPrintingsByName(selectedName) : []), [selectedName, getAllPrintingsByName]);
  const hasEditionPhase = printings.length > 1;

  const availablePhases = useMemo<AddCardPhase[]>(() => {
    const phases: AddCardPhase[] = isPreSelected ? [] : ['search'];
    if (hasEditionPhase || !selectedCard) phases.push('edition');
    phases.push('details');
    return phases;
  }, [isPreSelected, hasEditionPhase, selectedCard]);

  /** Returns true when a different card was picked (callers reset the instance form). */
  const selectCard = useCallback(
    (card: MinimalCard): boolean => {
      const isNewCard = !isSameCard(selectedCard, card);
      const cardPrintings = isNewCard ? getAllPrintingsByName(card.name) : printings;

      setSelectedCard(card);
      if (cardPrintings.length <= 1) {
        setSelectedPrinting(cardPrintings[0] ?? card);
        setPhase('details');
      } else {
        if (isNewCard) setSelectedPrinting(null);
        setPhase('edition');
      }
      return isNewCard;
    },
    [selectedCard, printings, getAllPrintingsByName],
  );

  const selectPrinting = useCallback((printing: MinimalCard) => {
    setSelectedPrinting(printing);
    setPhase('details');
  }, []);

  const canVisit = useCallback(
    (target: AddCardPhase) => {
      if (!availablePhases.includes(target)) return false;
      if (target === 'edition') return Boolean(selectedCard);
      if (target === 'details') return Boolean(selectedPrinting);
      return true;
    },
    [availablePhases, selectedCard, selectedPrinting],
  );

  const previousPhase = availablePhases[availablePhases.indexOf(phase) - 1] ?? null;

  const goBack = useCallback(() => {
    if (previousPhase) setPhase(previousPhase);
  }, [previousPhase]);

  const goToPhase = useCallback(
    (target: AddCardPhase) => {
      if (canVisit(target)) setPhase(target);
    },
    [canVisit],
  );

  /** After "Add & next": fresh search, or back to editions of the same card when pre-selected. */
  const resetForNext = useCallback(() => {
    if (isPreSelected) {
      setPhase(hasEditionPhase ? 'edition' : 'details');
      return;
    }
    setSelectedCard(null);
    setSelectedPrinting(null);
    setSearchResetKey((key) => key + 1);
    setPhase('search');
  }, [isPreSelected, hasEditionPhase]);

  return {
    phase,
    availablePhases,
    selectedCard,
    selectedPrinting,
    printings,
    searchResetKey,
    previousPhase,
    canVisit,
    selectCard,
    selectPrinting,
    goBack,
    goToPhase,
    resetForNext,
  };
}
